/**
 * Meeting and Scheduling Types
 */

// Meeting types (matches backend structure)
export interface Meeting {
  id: number;
  contract_id: number;
  scheduled_datetime: string; // ISO datetime string
  assessor_invitation_status: MeetingInvitationStatus;
  contract_owner_invitation_status: MeetingInvitationStatus;
  contract_manager_invitation_status: MeetingInvitationStatus;
  join_url?: string; // Teams meeting URL
  created_at: string;
  updated_at: string;
}

export type MeetingInvitationStatus = 'sent' | 'accepted' | 'declined';

// Tooltip contract data for meeting tooltips
export interface TooltipContractData {
  assessor_name?: string;
  assessor_id?: number;
  contract_managers?: Array<{ id: number; name: string }>;
}

// Schedule Item Contract type (flattened contract within ScheduleItem)
export interface ScheduleItemContract {
  id: number;
  contract_number: string;
  name: string;
  tier: string;
  supplier_id: number;
  supplier_name: string;
  contract_type: string;
  start_date: string;
  end_date: string;
  amount: number;
  cost_center?: string | null;
  division: string;
  status: string;
  contract_owners: Array<{
    id: number;
    name: string;
  }>;
  contract_managers: Array<{
    id: number;
    name: string;
  }>;
  created_at: string;
  updated_at: string;
  contract_header_description: string;
  contract_category: string;
  target_date: string;
  urgency: string;
  state: string;
  assessment?: {
    id: number;
    name: string;
    number: number;
    status: string;
    evidence_status?: string | null;
    evidence_due_date?: string | null;
  } | null;
  conflicts?: Array<{
    id: number;
    conflict_type_code: string;
    conflict_type_name: string;
    entity_type: 'contract' | 'meeting' | 'user' | 'assessment';
    entity_id: number;
    status: 'open' | 'acknowledged' | 'resolved';
    severity: 'info' | 'warning' | 'error' | 'blocking';
    context: Record<string, unknown>;
    detected_at: string;
  }>;
}

// Schedule Item types (for Scheduler Dashboard grouping)
export interface ScheduleItem {
  id: number | null; // null for single contracts until scheduled
  assessor: {
    id: number;
    name: string;
  } | null;
  processed_by: {
    id: number;
    name: string;
  } | null;
  meeting: {
    meeting_id: string;
    scheduled_datetime: string;
    status: string;
    join_url?: string;
    notes?: string | null;
    reschedule_reason?: string | null;
    previous_scheduled_datetime?: string | null;
    invites: {
      user_id: number;
      status: string;
    }[];
  } | null;
  contracts: ScheduleItemContract[];
}

// Meeting date suggestions (matches backend format)
export interface MeetingDateSuggestion {
  startDateTime: string; // ISO datetime string
  endDateTime: string; // ISO datetime string
  timeZone: string; // Time zone of the slot
  confidence: 'high' | 'medium' | 'low'; // Confidence level of the availability
  organizerAvailability: string; // Organizer's availability status
  attendeeAvailability: Array<{
    email: string;
    availability: string;
  }>; // Availability status of each attendee
  suggestionReason: string; // Reason for the suggestion
  score: number; // Confidence score (0-100)
}

// Meeting cancellation and reschedule reasons
export const MEETING_CANCELLATION_REASON = {
  CONTRACT_CANCELLED: 'ContractCancelled',
  CONTRACT_OUT_OF_SCOPE: 'ContractOutOfScope',
  CM_CHANGED: 'CMChanged',
  NEW_CM_IDENTIFICATION_NEEDED: 'NewCMIdentificationNeeded',
  OTHER: 'Other'
} as const;

export type MeetingCancellationReason = (typeof MEETING_CANCELLATION_REASON)[keyof typeof MEETING_CANCELLATION_REASON];

export const MEETING_RESCHEDULE_REASON = {
  ASSESSOR_CONFLICT: 'AssessorConflict',
  CM_NO_SHOW: 'CMNoShow',
  CM_REQUESTED: 'CMRequested',
  CONTRACT_SERVICES_NOT_STARTED: 'ContractServicesNotStarted',
  OTHER: 'Other'
} as const;

export type MeetingRescheduleReason = (typeof MEETING_RESCHEDULE_REASON)[keyof typeof MEETING_RESCHEDULE_REASON];

export const CANCELLATION_REASON_LABELS: Record<MeetingCancellationReason, string> = {
  [MEETING_CANCELLATION_REASON.CONTRACT_CANCELLED]: 'Contract Cancelled',
  [MEETING_CANCELLATION_REASON.CONTRACT_OUT_OF_SCOPE]: 'Contract Out of Scope',
  [MEETING_CANCELLATION_REASON.CM_CHANGED]: 'CM Changed',
  [MEETING_CANCELLATION_REASON.NEW_CM_IDENTIFICATION_NEEDED]: 'New CM Identification Needed',
  [MEETING_CANCELLATION_REASON.OTHER]: 'Other'
};

export const RESCHEDULE_REASON_LABELS: Record<MeetingRescheduleReason, string> = {
  [MEETING_RESCHEDULE_REASON.ASSESSOR_CONFLICT]: 'Assessor Conflict',
  [MEETING_RESCHEDULE_REASON.CM_NO_SHOW]: 'CM No-Show',
  [MEETING_RESCHEDULE_REASON.CM_REQUESTED]: 'CM Requested',
  [MEETING_RESCHEDULE_REASON.CONTRACT_SERVICES_NOT_STARTED]: 'Contract Services Not Started',
  [MEETING_RESCHEDULE_REASON.OTHER]: 'Other'
};
